import { useForm, useWatch } from "react-hook-form";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Button } from "@/components/Button/Button";
import { Form } from "@/components/Form/Form";
import { FormContent } from "@/components/Form/FormContent";
import { FormField } from "@/components/Form/FormField";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormTextArea } from "@/components/Form/FormTextArea";
import { InfoCircleIcon } from "@/components/Icons/InfoCircleIcon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { PreviewLayout } from "@/components/Layouts/PreviewLayout";
import { Notice } from "@/components/Notice/Notice";
import { DocumentPaper } from "@/components/Paper/DocumentPaper";
import { Phone } from "@/components/Phone/Phone";
import { getFlagIcon } from "@/helpers/flags";
import { createImageSizeRule, createImageTypeRule } from "@/helpers/rules";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { OnboardingScreenAppPreview } from "../List/components/OnboardingScreenAppPreview";

interface CreateOrEditOnboardingScreenFormValues {
  title: string;
  subtitle: string;
  image: FormImage[];
}

export interface LayoutProps {
  isEditMode: boolean;
  defaultValues?: Partial<CreateOrEditOnboardingScreenFormValues>;
  styling: {
    bgColor?: string;
    textColor?: string;
  };
  isSubmitting: boolean;
  onSubmit: (data: CreateOrEditOnboardingScreenFormValues) => void;
}
export function Layout({ isEditMode, defaultValues, styling, isSubmitting, onSubmit }: LayoutProps): React.ReactNode {
  const formMethods = useForm<CreateOrEditOnboardingScreenFormValues>({
    defaultValues,
  });
  const { t } = useTranslation();
  const slug = useSlug();

  const title = isEditMode ? t("page.onboarding-screens.edit.title") : t("page.onboarding-screens.new.title");

  return (
    <DocumentPaper
      theme="wide"
      title={title}
      subTitle={
        <Breadcrumbs
          pages={[
            {
              name: t("page.onboarding-screens.title"),
              to: routes.onboardingScreens.list({ slug }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form formMethods={formMethods} onSubmit={onSubmit}>
        <PreviewLayout
          previewTitleText={t("page.onboarding-screens.preview.title")}
          showPreviewButtonText={t("page.onboarding-screens.preview.open")}
          preview={<Preview styling={styling} />}
        >
          <CreateEditForm isEditMode={isEditMode} isSubmitting={isSubmitting} />
        </PreviewLayout>
      </Form>
    </DocumentPaper>
  );
}

const TITLE_MAX_LENGTH = 50;
const SUBTITLE_MAX_LENGTH = 150;

function CreateEditForm({ isEditMode, isSubmitting }: { isEditMode: boolean; isSubmitting: boolean }) {
  const sessionUser = useSessionUser();
  const { t } = useTranslation();

  return (
    <div className="flex h-full flex-col justify-between">
      <FormContent>
        <Notice
          icon={<InfoCircleIcon />}
          styling="filled"
          type="info"
          message={t("common.label.your-input-will-be-auto-translated")}
        />
        <FormField
          htmlFor="title-input"
          label={t("page.onboarding-screens.form.title")}
          actions={getFlagIcon({
            languageId: sessionUser.language.id,
            width: 16,
          })}
        >
          <FormInput<CreateOrEditOnboardingScreenFormValues>
            className="max-w-full sm:w-96"
            id="title-input"
            name="title"
            rules={{
              maxLength: {
                value: TITLE_MAX_LENGTH,
                message: t("components.form.error.max-length", {
                  length: TITLE_MAX_LENGTH,
                }),
              },
            }}
          />
        </FormField>
        <FormField
          htmlFor="subtitle-input"
          label={t("page.onboarding-screens.form.subtitle")}
          actions={getFlagIcon({
            languageId: sessionUser.language.id,
            width: 16,
          })}
        >
          <FormTextArea<CreateOrEditOnboardingScreenFormValues>
            className="max-w-full sm:w-96"
            data-testid="subtitle-textarea"
            id="subtitle-input"
            name="subtitle"
            rules={{
              maxLength: {
                value: SUBTITLE_MAX_LENGTH,
                message: t("components.form.error.max-length", {
                  length: SUBTITLE_MAX_LENGTH,
                }),
              },
            }}
          />
        </FormField>
        <FormField htmlFor="image" label={t("page.onboarding-screens.form.image")} required>
          <FormImageInput<CreateOrEditOnboardingScreenFormValues, "image">
            name="image"
            id="image-input"
            rules={{
              required: t("components.form.error.required", {
                inputName: t("page.onboarding-screens.form.image"),
              }),
              validate: {
                size: createImageSizeRule({
                  t,
                }),
                type: createImageTypeRule({
                  t,
                }),
              },
            }}
          />
        </FormField>
      </FormContent>
      <Button type="submit" isLoading={isSubmitting}>
        {isEditMode ? t("page.onboarding-screens.edit.submit") : t("page.onboarding-screens.new.submit")}
      </Button>
    </div>
  );
}
function Preview({ styling }: { styling: LayoutProps["styling"] }) {
  const { title, subtitle, image } = useWatch<CreateOrEditOnboardingScreenFormValues>();

  return (
    <Phone>
      <OnboardingScreenAppPreview
        imageUrl={image?.[0]?.url || ""}
        title={title}
        subtitle={subtitle}
        styling={styling}
      />
    </Phone>
  );
}
